// 時間要用哪個時區顯示（plan/vms-web-v0.md ORG-02、W16）：使用者偏好 vms.user_prefs.tz_display（site = 場域時區，預設；browser = 瀏覽器）。
// 場域時區 = 家戶的 timezone（家戶沒設就用錨點場域的；GET /v1/me/devices 的 orgs[].homes[].timezone 帶回來）。
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useScope } from './scope';
import { vmsApi } from './planeApi';
import { usePrefs } from '../features/map/useMapData';
import { browserTz, validTz } from './tz';

export function useTzPref() {
  const { planes } = useScope();
  const api = planes[0]?.api;
  const prefs = usePrefs(api);
  const qc = useQueryClient();
  const mode: 'site' | 'browser' = prefs.data?.tz_display === 'browser' ? 'browser' : 'site';
  const setMode = useCallback(
    async (m: 'site' | 'browser') => {
      if (!api) return;
      await vmsApi.setPrefs(api, { ...(prefs.data ?? {}), tz_display: m }).catch(() => {});
      await qc.invalidateQueries({ queryKey: ['vms-prefs'] });
    },
    [api, prefs.data, qc],
  );
  return { mode, setMode };
}

/** 這個家戶的時間要用的時區。 */
export function useHomeTz(homeId: string | undefined): { tz: string; siteTz: string | null; mode: 'site' | 'browser'; setMode(m: 'site' | 'browser'): Promise<void> } {
  const { homes } = useScope();
  const { mode, setMode } = useTzPref();
  const h = homes.find((x) => x.home_id === homeId);
  const raw = (h as { timezone?: string } | undefined)?.timezone;
  const siteTz = validTz(raw) ? raw : null;
  return { tz: mode === 'site' && siteTz ? siteTz : browserTz(), siteTz, mode, setMode };
}

/** 多家戶的列表（事件、總覽）：每一列用它家戶的時區。 */
export function useTzResolver(): (homeId: string | undefined) => string {
  const { homes } = useScope();
  const { mode } = useTzPref();
  return useCallback(
    (homeId) => {
      if (mode === 'browser') return browserTz();
      const raw = (homes.find((h) => h.home_id === homeId) as { timezone?: string } | undefined)?.timezone;
      return validTz(raw) ? raw : browserTz();
    },
    [homes, mode],
  );
}
